import "./ClientMedicalHistoryFormComponent.scss";
import * as Yup from "yup";
import {IClientMedicalHistoryForm} from "../../../../shared/models/client.model";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {Misc} from "../../../../constants";
import CardComponent from "../../../../shared/components/card/CardComponent";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {IMedicalHistoryOption} from "../../../../shared/models/common.model";
import CheckBoxComponent from "../../../../shared/components/form-controls/check-box/CheckBoxComponent";
import FormikTextAreaComponent
    from "../../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import FormikCheckBoxComponent
    from "../../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import SearchComponent from "../../../../shared/components/search/SearchComponent";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";
import AcknowledgementFormComponent from "../../acknowledgement-form/AcknowledmentFormComponent";
import ModalComponent from "../../../../shared/components/modal/ModalComponent";

interface ClientMedicalHistoryFormComponentProps {
    onCancel: () => void;
    onNext?: () => void;
    clientId: string;
    onSave: (clientMedicalHistoryDetails: any) => void;
    clientMedicalHistoryDetails: any;
    isClientMedicalHistoryDetailsLoaded: boolean | undefined;
    isClientMedicalHistoryDetailsLoading: boolean | undefined;
    isClientMedicalHistoryDetailsLoadingFailed: boolean | undefined;
}

const ClientMedicalHistoryValidationSchema = Yup.object({
    medical_history: Yup.object({
        isCustomOption: Yup.boolean().nullable(),
        questions: Yup.array().nullable().when("isCustomOption", {
            is: false,
            then: Yup.array().min(1, 'Medical history is required'),
            otherwise: Yup.array().nullable()
        }),
        comments: Yup.string().when("isCustomOption", {
            is: true,
            then: Yup.string().required('Comments is required'),
            otherwise: Yup.string().nullable()
        })
    }),
});

const ClientMedicalHistoryInitialValues: IClientMedicalHistoryForm = {
    medical_history: {
        questions: [],
        isCustomOption: false,
        comments: ""
    }
};

const ClientMedicalHistoryFormComponent = (props: ClientMedicalHistoryFormComponentProps) => {

    const {
        onCancel, onSave,
        clientId,
        clientMedicalHistoryDetails,
        isClientMedicalHistoryDetailsLoaded,
        isClientMedicalHistoryDetailsLoading,
        isClientMedicalHistoryDetailsLoadingFailed
    } = props;
    const {medicalHistoryOptionsList} = useSelector((state: IRootReducerState) => state.staticData);
    const [clientMedicalHistoryInitialValues, SetClientMedicalHistoryInitialValues] = useState<IClientMedicalHistoryForm>(_.cloneDeep(ClientMedicalHistoryInitialValues));
    const [isClientMedicalHistorySavingInProgress, setIsClientMedicalHistorySavingInProgress] = useState(false);
    const [searchKey, setSearchKey] = useState<string>("");
    const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);
    const dispatch = useDispatch();


    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        SetClientMedicalHistoryInitialValues(values);
        setIsAcknowledgementModalOpen(true);
    }, [])

    const handleAcknowledgementFormSubmit = useCallback((acknowledgementValues:any) => {
        const payload = {
            ...CommonService.removeKeysFromJSON(_.cloneDeep(clientMedicalHistoryInitialValues), ['questions_details']),
            ...acknowledgementValues,
            update_type: "profile",
            client_id: clientId
        };
        setIsClientMedicalHistorySavingInProgress(true);
        CommonService._client.ClientMedicalHistoryMedicalHistoryDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<IClientMedicalHistoryForm>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setIsClientMedicalHistorySavingInProgress(false);
                SetClientMedicalHistoryInitialValues(_.cloneDeep(acknowledgementValues));
                onSave(response);
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error[Misc.API_RESPONSE_MESSAGE_KEY], 'error');
                setIsClientMedicalHistorySavingInProgress(false);
            })
    }, [onSave,clientId,clientMedicalHistoryInitialValues]);

    useEffect(() => {
        if (clientMedicalHistoryDetails) {
            if (clientMedicalHistoryDetails?.medical_history?.comments) {
                clientMedicalHistoryDetails.medical_history.isCustomOption = true;
            }
            SetClientMedicalHistoryInitialValues({
                medical_history: clientMedicalHistoryDetails.medical_history || []
            });
        }
    }, [dispatch, clientMedicalHistoryDetails]);


    const handleMedicalHistoryOptionSelection = useCallback((optionId: string, selectedOptions: string[]) => {
        const options = _.cloneDeep(selectedOptions);
        const index = options?.findIndex((value: string) => value === optionId);
        if (index > -1) {
            options.splice(index, 1);
        } else {
            options.push(optionId);
        }
        return options;
    }, []);

    return (
        <div className={'client-medical-history-form-component'}>
            <>
                {
                    isClientMedicalHistoryDetailsLoading && <div>
                        <LoaderComponent/>
                    </div>
                }
                {
                    isClientMedicalHistoryDetailsLoadingFailed &&
                    <StatusCardComponent title={"Failed to fetch medical Details"}/>
                }
            </>
            {isClientMedicalHistoryDetailsLoaded && <>
                <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                                title={"Back"}/>


                <Formik
                    validationSchema={ClientMedicalHistoryValidationSchema}
                    initialValues={clientMedicalHistoryInitialValues}
                    onSubmit={onSubmit}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}>
                    {({values, setFieldError, setFieldTouched, setFieldValue, isValid, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <CardComponent>
                                <div className={'edit-medical-history-heading'}>
                                    Edit Medical History
                                </div>
                                <Form noValidate={true} className={"t-form"}>
                                    <div className={'edit-medical-provider-wrapper'}>

                                        <DetailsViewCardComponent title={"Medical History"}>
                                            <div className={'edit-medical-history-sub-heading'}>
                                                Have you ever had or do you currently have: (Check all that apply)
                                            </div>
                                            <div className="ts-row">
                                                <div className="ts-col-lg-6">
                                                    <SearchComponent value={searchKey}
                                                                     onChange={value => setSearchKey(value)}/>
                                                </div>
                                            </div>
                                            <div className={'d-flex'}>
                                                <div>
                                                    {
                                                        medicalHistoryOptionsList?.slice(0, 24)?.filter(item => item?.title?.toLowerCase()?.includes(searchKey?.trim()?.toLowerCase()))?.map((option: IMedicalHistoryOption) => {
                                                            return <div key={option?._id}>
                                                                <Field
                                                                    name={"medical_history.questions"}>
                                                                    {(field: FieldProps) => (
                                                                        <CheckBoxComponent
                                                                            id={'cb_' + option?.title}
                                                                            label={option?.title}
                                                                            // required={true}
                                                                            checked={field.field?.value?.indexOf(option._id) > -1}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.field?.name);
                                                                                setFieldValue(field.field?.name, handleMedicalHistoryOptionSelection(option._id, field.field?.value));
                                                                                validateForm();
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        })
                                                    }
                                                </div>
                                                <div className={'ts-col-1'}/>
                                                <div>
                                                    {
                                                        medicalHistoryOptionsList?.slice(24, 48)?.filter(item => item?.title?.toLowerCase()?.includes(searchKey?.trim()?.toLowerCase()))?.map((option: IMedicalHistoryOption) => {
                                                            return <div key={option?._id}>
                                                                <Field
                                                                    name={"medical_history.questions"}>
                                                                    {(field: FieldProps) => (
                                                                        <CheckBoxComponent
                                                                            id={'cb_' + option?.title}
                                                                            label={option?.title}
                                                                            // required={true}
                                                                            checked={field.field?.value?.indexOf(option._id) > -1}
                                                                            onChange={() => {
                                                                                setFieldTouched(field.field?.name);
                                                                                setFieldValue(field.field?.name, handleMedicalHistoryOptionSelection(option._id, field.field?.value));
                                                                                validateForm();
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        })
                                                    }
                                                </div>
                                            </div>

                                            <div className="ts-row">
                                                <div className="ts-col-md-6">
                                                    <Field
                                                        name={"medical_history.isCustomOption"}>
                                                        {(field: FieldProps) => (
                                                            <FormikCheckBoxComponent
                                                                formikField={field}
                                                                label={"Any other illnesses or conditions not listed above?"}
                                                                onChange={(isChecked) => {
                                                                    if (!isChecked) {
                                                                        setFieldValue('medical_history.comments', "");
                                                                        setFieldError('medical_history.comments', undefined);
                                                                        setTimeout(() => { // TODO solve fool proof
                                                                            validateForm();
                                                                        }, 10);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Field>
                                                </div>
                                            </div>
                                            {values.medical_history?.isCustomOption && <div className="ts-row">
                                                <div className="ts-col-10">
                                                    <Field name={`medical_history.comments`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikTextAreaComponent
                                                                    label={"Other Illnesses/Conditions"}
                                                                    placeholder={"Enter your comments here"}
                                                                    disabled={!values.medical_history?.isCustomOption}
                                                                    required={values.medical_history?.isCustomOption}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    rows={3}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>}
                                        </DetailsViewCardComponent>
                                    </div>
                                    <div className="t-form-actions mrg-bottom-0 mrg-top-30">
                                        <ButtonComponent
                                            id={"home_btn"}
                                            variant={"outlined"}
                                            onClick={onCancel}
                                            disabled={isClientMedicalHistorySavingInProgress}
                                            color={'accent'}
                                        >
                                            Cancel
                                        </ButtonComponent>&nbsp;
                                        <ButtonComponent
                                            id={"save_next_btn"}
                                            className={'mrg-left-25'}
                                            isLoading={isClientMedicalHistorySavingInProgress}
                                            disabled={isClientMedicalHistorySavingInProgress || !isValid}
                                            type={"submit"}
                                            color={'accent'}
                                        >
                                            {isClientMedicalHistorySavingInProgress ? "Saving" : 'Next'}
                                        </ButtonComponent>

                                    </div>
                                </Form>
                            </CardComponent>
                        )
                    }}
                </Formik>
            </>
            }
            <ModalComponent isOpen={isAcknowledgementModalOpen}>
                <AcknowledgementFormComponent setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
                                              isLoading={isClientMedicalHistorySavingInProgress}
                                              onSubmit={handleAcknowledgementFormSubmit}/>
            </ModalComponent>
        </div>
    );

};

export default ClientMedicalHistoryFormComponent;
